import { defineStore } from "pinia";
import apiclient from "@/services/apiclient";
import getPlannerTasks, {
  type PlannerTask,
} from "@/graphql/services/planner/getPlannerTasks";
import { LocalStorageKeys } from "@/enums";
import {
  CreatePlannerTaskDocument,
  EditPlannerTaskDocument,
  GetActorsDocument,
  PlannerCategoriesDocument,
  Profile_Type,
  Audit_Event_Type,
} from "@/graphql/generated-types";
import { filterStorageService } from "@/services/filterStorageService/FilterStorageService";
import filters, { getActorsForEntry } from "@/stores/filters";
import { createProvider } from "@/vue-apollo";
import {
  normalizeTask,
  toPlannerTaskCreateInput,
  toPlannerTaskEditInput,
} from "@/stores/helpers";
import { useAuthStore } from "@/stores/auth.module";
import { usePlannerCustomFilters } from "@/composables/usePlannerCustomFilters";
import {
  addDays,
  format,
  formatISO,
  parse,
  parseISO,
  startOfISOWeek,
} from "date-fns";
import api from "@/api";
import type { AuditLogFragment } from "@/graphql";

// Names of modules which entries can be added into the calendar.
export const MODULE_TYPES = ["meeting", "booking", "meal", "activity"] as const;
export type ModuleType = (typeof MODULE_TYPES)[number];

// Actor of entities passed with module entities.
type ModuleTypeEntityActor = {
  id: number;
  [key: string]: any; // Has more but only typing what we need for now.
} | null;

// The actor
export type ModuleEntity = {
  type: ModuleType;
  actorCounts: {
    resident: number;
    employee: number;
    department: number;
  };
  actorIds: {
    resident: string[];
    employee: string[];
    department: string[];
  };
  actors: {
    resident: ModuleTypeEntityActor;
    employee: ModuleTypeEntityActor;
    department: ModuleTypeEntityActor;
  };
  [key: string]: any; // Has more but only typing what we need for now.
};

type UpdateEntitiesParams = {
  date: string;
  types: ModuleType[];
  showDays: number;
};

export type AuditLogActor = {
  id: string;
  [key: string]: unknown;
} | null;

export type MappedAuditLogs = {
  created: string;
  entityType: string;
  entityId: string;
  event: Audit_Event_Type;
  actor?: AuditLogActor;
};

// Note the keys use plural form in some places and singular in notes.
const PlannerFilterOptions = [
  "categories",
  "departments",
  "employees",
  "residents",
  "status",
  "userTypes",
] as const;
export type PlannerFilterOption = (typeof PlannerFilterOptions)[number];
export type ActorFilterOptions = Omit<PlannerFilterOption, "status">;

export const ACTOR_TYPES = ["resident", "employee", "department"] as const; // List of used entities.
export type PlannerEntityType = (typeof ACTOR_TYPES)[number];

export type Actor = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  imagePath: string;
  color: string | null;
  [key: string]: any; // There's more but for now they don't need to be typed.
};

// This naming is mostly used in the UI components.
export type AssignableEntities =
  | "resident"
  | "employee"
  | "department"
  | "unassigned";
export type TaskStatus = "completed:0" | "completed:1";
export type ActorsObject = Record<PlannerEntityType, Actor | null>;

// Internal filters use plural except for in userTypes where its singular.
export type InternalFilters = {
  categories: string[];
  departments: string[];
  employees: string[];
  residents: string[];
  status: TaskStatus[];
  userTypes: PlannerEntityType[]; // UI does not allow 'unassigned' to be selected, but it's used elsewhere.
};

export interface IPlannerState {
  internalFilters: InternalFilters;
  actors: ActorsObject;
  selectedDate: Date;
  entries: Array<PlannerTask | ModuleEntity>; // Planner tasks and module entities.
  plannerTasks: PlannerTask[];
  selectedEntry: null | any;
  selectedSubtasks: any[];
  loadedEntityTypes: any[];
  enabledModules: ModuleType[];
  showOverdue: boolean;
  isActorsReady: boolean;
  actorsSubscriptions: any[];
  useVismaGatWs: boolean;
  setSelectedSubtasks: any[];
  pickingFormat: ViewType;
  loading: boolean;
  isCustomFiltersSet: boolean;
  customFilterValues: string[];
}

// Planner task || Module entity after being categorized in weekview.
export type PlannerEntity = {
  uniqueId: string;
  entries: any; // Workaround to save time.
} & (Partial<PlannerTask> | ModuleEntity);

// Object with keys of AssignableEntities and lists of PlannerEntities.
export type CategorizedFilteredEntries = Record<
  AssignableEntities,
  PlannerEntity[]
>;

export interface IFetchTasksDaysProps {
  date: string;
  showDays: number;
}

export type ViewType = "week" | "day";

type ActorTypes = "resident" | "employee" | "department";

const getDefaultPickingFormat = (): ViewType => {
  const storageKey = LocalStorageKeys.DefaultPlannerView;

  const defaultView = localStorage.getItem(storageKey);

  if (!defaultView) {
    return "day";
  }

  return defaultView as ViewType;
};

export const usePlannerStore = defineStore("planner", {
  state: () => {
    return {
      pickingFormat: getDefaultPickingFormat(),
      internalFilters: filterStorageService.getPlannerFilters(),
      actors: {
        resident: null,
        employee: null,
        department: null,
      },
      selectedDate: new Date(), // All entries (tasks + entities).
      entries: [], // The currently selected task to show in drawer.
      selectedEntry: null, // The subtasks of the currently selected task.
      selectedSubtasks: [], // Entity types that have been fetched from the server, but not necessarily shown.
      loadedEntityTypes: [], // Entity types that should currently be shown.
      enabledModules: filterStorageService.getModules(),
      showOverdue: false,
      isActorsReady: false, // We need actors to show task. This variable checks if the actors are loaded
      actorsSubscriptions: [], // If actors are not ready, we create a simple callback queues to fetch tasks.
      useVismaGatWs: false,
      setSelectedSubtasks: [],
      loading: false,
      isCustomFiltersSet: false,
      customFilterValues: [],
    } as unknown as IPlannerState;
  },
  getters: {
    // Returns a specific filter type
    filters(state: IPlannerState) {
      return (key: PlannerFilterOption) => state.internalFilters[key] || [];
    },

    // Day-view list of entries with all filters applied.
    filteredEntries(state) {
      return filters.filteredEntries(state);
    },

    // Week-view list of entries with all filters applied.
    categorizedFilteredEntries(
      state,
    ): (listOfDates: string[]) => CategorizedFilteredEntries {
      return (dayList: string[]) => {
        return filters.categorizeFilteredEntries(state as any, dayList);
      };
    },

    departments(state) {
      return state?.actors?.department || [];
    },

    // Returns a single Actor, which is fine, since only a single actor pr. actorType can be added to a task.
    actor:
      (
        state: IPlannerState,
      ): ((actorType: PlannerEntityType, id: string) => null | Actor) =>
      (actorType, id) => {
        if (!state.actors || !state.actors[actorType]) return;
        return state.actors[actorType].find((actor: Actor) => actor.id === id);
      },
  },
  actions: {
    // Update a filter option with values to include.
    changeFilter(values: string[], key: PlannerFilterOption) {
      this.setFilters({
        values, // e.g ['1','2','resident']
        key, // e.g 'userTypes' | 'employees'
      });
    },

    setCustomFilters(values: string[], filters: any[]) {
      const plannerFilters = usePlannerCustomFilters();

      this.customFilterValues = values;

      const enable = plannerFilters.filters.value.find(
        (x) => x.id == values[0],
      );

      if (enable) {
        this.changeFilter(
          (enable.assigneeTypes || []).map((item) => item.toLowerCase()),
          "userTypes",
        );
        this.changeFilter(enable.categories || [], "categories");
        this.changeFilter(enable.departments || [], "departments");
        this.changeFilter(enable.employees || [], "employees");
        this.changeFilter(enable.residents || [], "residents");
      } else {
        filters.forEach((filter: any) => {
          this.changeFilter([], filter.key);
        });
      }

      // Module Filters are currently not supported with Custom Filters but will be in the future.
      // If implementing before rewrite, extend the logic here. For now, clear Module Filters when toggling Custom Filters.
      // TODO: rewrite: use selectedDate and pickingFormat in store instead of getting and passing it back from store.
      const { pickingFormat, selectedDate } = this;
      const showDays = pickingFormat === "day" ? 1 : 7;
      this.updateEntities({
        date: format(selectedDate, "yyyy-MM-dd"),
        types: [],
        showDays,
      });
    },

    // Checks if the id is currently in a filter.
    isActorIdInFilter(actorIdToFind: string): boolean {
      const { departments, employees, residents } = this.internalFilters;
      return (
        departments.includes(actorIdToFind) ||
        employees.includes(actorIdToFind) ||
        residents.includes(actorIdToFind)
      );
    },
    async toggleDrawer(entry: any) {
      if (
        entry &&
        (this.selectedEntry === null ||
          this.selectedEntry.listKey !== entry.listKey)
      ) {
        this.openDrawer(entry);
      } else {
        this.closeDrawer();
      }
    },

    async openDrawer(entry: any) {
      this.setSelectedEntry(entry);
      this.fetchSubtasks(entry);
    },

    async closeDrawer() {
      this.setSelectedEntry(null);
      this.setSelectedSubtasks = [];
    },

    async fetchTasksDays({ date, showDays }: IFetchTasksDaysProps) {
      const dateFrom = formatISO(parse(date, "yyyy-MM-dd", new Date()));
      const dateTo = formatISO(
        addDays(parse(date, "yyyy-MM-dd", new Date()), showDays),
      );
      try {
        this.loading = true;
        const tasks = await getPlannerTasks({ dateFrom, dateTo });
        this.setEntries(tasks);
        return tasks;
      } finally {
        this.loading = false;
      }
    },

    async fetchAuditLogs({ entityId, entityType }: any) {
      // Add profile to log. Tasks have already been loaded at this point
      // so we know actors will be there.
      const auditLogMapper = (row: AuditLogFragment) => {
        const { created, entityType, entityId, event } = row;
        const log: MappedAuditLogs = {
          created,
          entityType,
          entityId,
          event,
          actor: undefined,
        };

        if (row.profileType !== undefined && row.profileId !== undefined) {
          const profileType: ActorTypes = (
            row.profileType || Profile_Type.Employee
          ).toLowerCase() as ActorTypes;
          log.actor = this.actors[profileType]?.find(
            (v: any) => v.id == row.profileId,
          );
        }

        return log as MappedAuditLogs;
      };

      const auditLogs = await api.planner.getAuditLog(entityId, entityType);
      return auditLogs.map(auditLogMapper);
    },

    async createPlannerTask(task: any) {
      return createProvider()
        .defaultClient.query({
          query: CreatePlannerTaskDocument,
          variables: {
            plannerTask: toPlannerTaskCreateInput(task),
          },
        })
        .catch((error) => {
          // oxlint-disable-next-line no-console
          console.log("error", error);
          return false;
        });
    },

    async editPlannerTask(task: any) {
      return createProvider()
        .defaultClient.query({
          query: EditPlannerTaskDocument,
          variables: {
            plannerTaskId: task.id,
            plannerTask: toPlannerTaskEditInput(task),
          },
        })
        .catch((error) => {
          // oxlint-disable-next-line no-console
          console.log("error", error);
          return false;
        });
    },

    callbackWhenActorsReady(callback: any) {
      // If actors are not ready, wait in the queue
      if (this.isActorsReady === false) {
        this.actorsSubscriptions.push(callback);
      } else {
        // Otherwise go ahead
        callback();
      }
    },

    callAllActorsSubscriptions() {
      // Call of functions that waits in the queue
      this.actorsSubscriptions.forEach((callback: any) => callback());
      // We can clean it now.
      this.actorsSubscriptions = [];
    },

    /**
     * Shorthand for single-day fetchTasksDays
     */
    async fetchTasks({ date, showDays }: IFetchTasksDaysProps) {
      return new Promise((resolve) => {
        // If the actors are ready, we can fetch the tasks safely.
        return this.callbackWhenActorsReady(async () => {
          const results = await this.fetchTasksDays({
            date: date,
            showDays,
          });

          resolve(results);
        });
      });
    },

    async fetchSubtasks(entry: any) {
      if (entry.type !== "task") {
        return;
      }
      return apiclient
        .get(
          "planner/get-subtasks/" +
            (entry.isRepeat ? entry.repeatId + "/repeat" : entry.id),
        )
        .then((response: any) => {
          this.selectedSubtasks = response.data;
        })
        .catch((error: any) => {
          // oxlint-disable-next-line no-console
          console.log("error", error);
        });
    },

    async fetchSearchResults(searchTerm: any) {
      return apiclient
        .get("planner/search", { params: { q: searchTerm } })
        .then((response: any) => {
          const tasks: PlannerTask[] = [];
          response.data.forEach((task: any) => {
            task.actors = filters.getActorsForEntry(task.actorIds, this.actor);
            tasks.push(normalizeTask(task));
          });
          this.setEntries(tasks);
        });
    },

    /**
     * Update list of enabled entities which are the other modules in IBG. See type ModuleType.
     */
    async updateEntities({ date, types, showDays = 1 }: UpdateEntitiesParams) {
      filterStorageService.setModules(types);

      const previousEnabledModules = [...this.enabledModules];

      this.enabledModules = [...types];

      // Identify types that have been removed
      const typesRemoved = previousEnabledModules.filter(
        (type) => !types.includes(type),
      );

      // If something was removed, simply remove the data from all entries.
      if (typesRemoved.length > 0) {
        this.entries = this.entries.filter(
          // @ts-ignore for now as these types overlap but isn't implemented in ts yet due to time.
          (entry) => !typesRemoved.includes(entry.type),
        );

        // Remove from loadedEntityTypes to allow selecting again.
        this.loadedEntityTypes = this.loadedEntityTypes.filter(
          (type) => !typesRemoved.includes(type),
        );

        // Return before fetching duplicate data.
        return;
      }

      // If something was added, add it to list of module data to load.
      const toLoad = types.filter(
        (x: ModuleType) => !this.loadedEntityTypes.includes(x),
      );

      // Do now await here, to start loading data for selected modules simultaneously.
      toLoad.forEach((type: ModuleType) => {
        apiclient
          .get("planner/get-entities/" + type + "/" + date + "/" + showDays)
          .then((response: any) => {
            response.data.forEach((task: ModuleEntity) => {
              task.start = parseISO(
                task.start.replace(/(\.\d{3})\d+Z$/, "$1Z"),
              );
              task.end = parseISO(task.end.replace(/(\.\d{3})\d+Z$/, "$1Z"));

              task.listKey = task.type + task.id;

              task.color = {
                meeting: "#8c9992",
                booking: "#36909b",
                activity: "#7b6361",
                meal: "#326b74",
              }[task.type];
            });
            // Add immediately instead of waiting, since we want to fetch the next module entities if possible.
            this.addToEntries(response.data);
            this.loadedEntityTypes = this.loadedEntityTypes.concat(type);
          })
          .catch((error: any) => {
            // oxlint-disable-next-line no-console
            console.log("error", error);
          });
      });
    },

    async updateTaskActor({ entry, actor, actorType }: any) {
      const actors = {
        ...entry.actors,
        [actorType]: actor,
      };

      apiclient
        .put("planner/task-actor/" + entry.id + "/" + actorType, {
          actorId: actor ? actor.id : null,
        })
        .then((response: any) => {
          if (!this.selectedEntry) {
            // Don't bother getting the updated task if drawer is already closed
            return;
          }

          const index = this.entries.findIndex(
            (x: any) => x.id == this.selectedEntry?.id,
          );

          // Populate this property ourselves, as it is deprecated
          response.data.actorIds = {
            resident: actors.resident ? [String(actors.resident.id)] : [],
            employee: actors.employee ? [String(actors.employee.id)] : [],
            department: actors.department ? [String(actors.department.id)] : [],
          };

          response.data.actors = actors;

          const entry = normalizeTask(
            response.data,
            this.selectedEntry.type + this.selectedEntry.id,
          );

          this.setSpecificEntry({
            payload: entry,
            index,
          });

          this.setSelectedEntry({
            ...entry,
            actors: actors,
          });

          this.fetchSubtasks(entry);
        });
    },

    async toggleTaskCompleted(entry: PlannerTask) {
      const completed = entry.completed;
      this.setSelectedEntry({
        ...entry,
        completed: !completed,
        openSubtaskCount: completed ? this.selectedSubtasks.length : 0,
      });

      this.setSelectedSubtasksState(!completed);

      return apiclient
        .put("planner/task-complete/" + entry.id + "/" + (completed ? 0 : 1))
        .then((response: any) => {
          if (!this.selectedEntry) {
            // Don't bother getting the updated task if drawer is already closed
            return;
          }

          const { type: selectedEntryType, id: selectedEntryId } =
            this.selectedEntry;

          // If the task is a repeat, updating subtasks will create a
          // new actual task from the virtual one, meaning we will need
          // to update the task list.
          if (this.selectedEntry.isRepeat) {
            return apiclient
              .get("planner/get-task/" + response.data.entryId)
              .then((entryResponse: any) => {
                const index = this.entries.findIndex(
                  (x) => x.id == selectedEntryId,
                );

                // Re-add actors object after getting entry from backend
                entryResponse.data.actors = getActorsForEntry(
                  entryResponse.data.actorIds,
                  this.actor,
                );

                const entry = normalizeTask(
                  entryResponse.data,
                  selectedEntryType + selectedEntryId,
                );

                this.setSpecificEntry({
                  payload: entry,
                  index,
                });

                this.setSelectedEntry(entry);

                this.fetchSubtasks(entry);
              });
          }
        });
    },

    async toggleSubtaskCompleted(subtask: any) {
      const val = subtask.completed;
      const newSubtask = { ...subtask };
      newSubtask.completed = !val;

      const subtaskId = this.selectedEntry.isRepeat
        ? subtask.sortorder
        : subtask.id;

      // Do not set the already selected task data if it's a repeat,
      // as we need to re-fetch the data in that case, which is done later.
      if (!this.selectedEntry.isRepeat) {
        this.setSubtask(newSubtask);

        // Set task's completed status based on whether
        // all subtasks are completed when changing subtasks.
        const openSubtaskCount = this.selectedSubtasks.filter(
          (x: any) => !x.completed,
        ).length;
        this.setSelectedEntry({
          ...this.selectedEntry,
          completed: openSubtaskCount === 0,
          openSubtaskCount: openSubtaskCount,
        });
      }

      return apiclient
        .put(
          "planner/subtask-complete/" +
            this.selectedEntry.id +
            "/" +
            subtaskId +
            "/" +
            (newSubtask.completed ? 1 : 0),
        )
        .then((response: any) => {
          if (!this.selectedEntry) {
            // Don't bother getting the updated task if drawer is already closed
            return;
          }
          // If the task is a repeat, updating subtasks will create a
          // new actual task from the virtual one, meaning we will need
          // to update the task list.
          if (this.selectedEntry.isRepeat) {
            return apiclient
              .get("planner/get-task/" + response.data.entryId)
              .then((entryResponse: any) => {
                const index = this.entries.findIndex(
                  (x: any) => x.id == this.selectedEntry.id,
                );

                // Re-add actors object after getting entry from backend
                entryResponse.data.actors = filters.getActorsForEntry(
                  entryResponse.data.actorIds,
                  this.actor,
                );

                const entry = normalizeTask(
                  entryResponse.data,
                  this.selectedEntry.type + this.selectedEntry.id,
                );

                this.setSpecificEntry({
                  payload: entry,
                  index,
                });

                this.setSelectedEntry(entry);

                this.fetchSubtasks(entry);
              });
          }
        })
        .catch(() => {
          this.setSubtask(subtask);

          this.setSelectedEntry({
            ...this.selectedEntry,
            completed:
              this.selectedSubtasks.filter((x: any) => !x.completed).length ===
              0,
          });
        });
    },

    async setSelectedSubtasksState(val: any) {
      const subtasks = this.selectedSubtasks;
      const newSubtasks = subtasks.map((subtask: any) => {
        return {
          ...subtask,
          completed: val,
        };
      });
      this.selectedSubtasks = newSubtasks;
    },

    async fetchCategories() {
      return createProvider()
        .defaultClient.query({
          query: PlannerCategoriesDocument,
        })
        .then((response: any) => {
          return response.data.plannerCategories;
        })
        .catch((error: any) => {
          // oxlint-disable-next-line no-console
          console.log("error", error);
        });
    },

    async fetchActors() {
      const authStore = useAuthStore();

      return createProvider()
        .defaultClient.query({
          query: GetActorsDocument,
          variables: {
            filter: {
              institutions: [authStore.institutionId],
            },
          },
        })
        .then((response: any) => {
          // Process data
          const departments = response.data?.institution.departments
            .filter((row: any) => !row.screenOnly)
            .map((row: any) => {
              return {
                id: parseInt(row.id),
                firstName: row.name,
                lastName: null,
                displayName: row.name,
                imagePath: null,
                color: row.color,
              };
            });

          const residents = response.data?.profiles
            .filter((row: any) => row.type == "RESIDENT")
            .map((row: any) => {
              return {
                id: parseInt(row.id),
                firstName: row.firstName,
                lastName: row.lastName,
                displayName: row.displayName,
                imagePath: row.displayImage,
                color: null,
                settings: row.settings,
              };
            })
            .sort((a: any, b: any) =>
              a.displayName.localeCompare(b.displayName),
            );

          const employees = response.data?.profiles
            .filter((row: any) => row.type == "EMPLOYEE")
            .map((row: any) => {
              return {
                id: parseInt(row.id),
                firstName: row.firstName,
                lastName: row.lastName,
                displayName: row.displayName,
                imagePath: row.displayImage,
                color: null,
                settings: row.settings,
              };
            })
            .sort((a: any, b: any) =>
              a.displayName.localeCompare(b.displayName),
            );

          this.actors = {
            department: departments,
            employee: employees,
            resident: residents,
          };

          this.isActorsReady = true;
          this.callAllActorsSubscriptions();
        });
    },

    resetFilters(filters: InternalFilters) {
      this.internalFilters = filters;
      filterStorageService.removePlannerFilters(); // Reset cached filters if they are removed.
    },

    // Adds filter values to filter options
    // e.g which id's to add to filter option employee
    setFilters(payload: { values: string[]; key: PlannerFilterOption }) {
      // @ts-ignore internalFilter keys are of PlannerFilterOption but isn't coupled yet.
      this.internalFilters[payload.key] = payload.values;
      filterStorageService.setPlannerFilters(this.internalFilters);
    },

    setEntries(payload: PlannerTask[]) {
      payload.sort((a, b) => {
        const diff = a.start.getTime() - b.start.getTime();
        if (diff === 0) {
          return a.end.getTime() - b.end.getTime();
        }
        return diff;
      });

      // We need to set actors before set "entries". Because entries is rendered
      // automatically, and setting actors after setting entries causes re-render
      // issues. Also, we shouldn't do this over and over again during filtering
      // results.
      payload.forEach((entry) => {
        entry.actors = getActorsForEntry(entry.actorIds, this.actor);
      });

      this.entries = payload;
    },

    setSpecificEntry({ payload, index }: any) {
      this.entries.splice(index, 1, payload);
    },

    // Add module entities to entries which contains both Planner Tasks and Module Entities.
    addToEntries(payload: ModuleEntity[]) {
      this.entries = this.entries
        .concat(payload)
        .sort((a, b) => (a.start > b.start ? 1 : -1));
    },

    setSubtask(payload: any) {
      const i = this.selectedSubtasks.findIndex(
        (subtask: any) => payload.id === subtask.id,
      );
      this.selectedSubtasks[i] = payload;
    },

    /**
     * Also overwrites the relevant entry in entries.
     */
    setSelectedEntry(payload: any) {
      this.selectedEntry = payload;
      if (payload) {
        const i = this.entries.findIndex(
          (entry: any) =>
            payload.id === entry.id && payload.type === entry.type,
        );
        this.entries[i] = payload;
      }
    },

    setLoadedEntityTypes(payload: any) {
      this.loadedEntityTypes = payload;
    },

    setEnabledModules(payload: ModuleType) {
      // @ts-ignore for now as some typing hasn't been coupled 100 %
      this.enabledModules = payload;
    },

    setActors(payload: any) {
      this.actors = payload;
    },

    setShowOverdue(payload: any) {
      this.showOverdue = payload;
    },

    setDate(date: Date) {
      this.selectedDate = date;

      // Getting the items from the API
      this.getItems();
    },

    setViewType(viewType: ViewType) {
      this.pickingFormat = viewType;

      // Setting the user's selection to the local storage
      localStorage.setItem(LocalStorageKeys.DefaultPlannerView, viewType);

      // Getting the items from the API
      this.getItems();
    },

    async getItems() {
      const showDays = this.pickingFormat === "day" ? 1 : 7;
      let date = format(this.selectedDate, "yyyy-MM-dd");

      // In the week selection, we should use the start of the week.
      if (this.pickingFormat === "week") {
        date = format(
          startOfISOWeek(new Date(this.selectedDate)),
          "yyyy-MM-dd",
        );
      }

      await this.fetchTasks({ date, showDays });

      this.setLoadedEntityTypes([]);

      this.updateEntities({
        date: date,
        types: this.enabledModules,
        showDays,
      });
    },
  },
});
